/**
 * @module app.shared
 */

import { buildJsonPropertyPath } from 'app/shared/lib/utilities';
import { alwaysTrue } from 'app/shared/lib/noop';

type PropertyFilterFunction = (propertyKey: string | number, target: any, propertyPath: string[]) => boolean;

export interface ICompareObjectsOptions {
    returnDifferences: boolean;
    propertyFilter: PropertyFilterFunction;
}

export interface IDifference {
    path: string;
    left: any;
    right: any;
}

export interface ICompareObjectsResult {
    areEqual: boolean;
    differences: IDifference[];
}

function isDirectlyComparable(value: any): boolean {
    return (value === null || value === undefined || typeof value !== 'object');
}

function areDifferent(a: any, b: any, options: ICompareObjectsOptions, path: string[], results?: IDifference[]): boolean {
    // check reference/value equality
    if(a === b) { return false; }

    if(isDirectlyComparable(a) || isDirectlyComparable(b)) {
        if(results) {
            results.push({ path: buildJsonPropertyPath(path), left: a, right: b });
        }
        return true;
    }

    if(Array.isArray(a)) {
        if(Array.isArray(b)) {
            if(a.length !== b.length) {
                if(results) {
                    results.push({ path: buildJsonPropertyPath(path), left: a, right: b });
                }
                return true;
            }

            let found: boolean = false;
            for(let index = 0; index < a.length; index++) {
                if(!options.propertyFilter(index, a, path)) { continue; }

                path.push(index.toString());
                const result = areDifferent(a[index], b[index], options, path, results);
                path.pop();
                if(result) {
                    found = true;
                    if(!results) { return found; } // short circuit if not tracking results
                }
            }

            return found;
        }
        if(results) {
            results.push({ path: buildJsonPropertyPath(path), left: a, right: b });
        }
        return true;
    }

    if(typeof a === 'object') {
        if(typeof b === 'object') {

            let found: boolean = false;
            for(const key in a) {
                if(!options.propertyFilter(key, a, path)) { continue; }

                const aVal = a[key];
                const bVal = b[key];

                path.push(key);
                const result = areDifferent(aVal, bVal, options, path, results);
                path.pop();
                if(result) {
                    found = true;
                    if(!results) { return found; } // short circuit if not tracking results
                }
            }
            for(const key in b) {
                if(key in a) { continue; }
                if(!options.propertyFilter(key, b, path)) { continue; }

                found = true;
                if(!results) { return found; } // short circuit if not tracking results
            }

            return found;
        }

        return true;
    }

    return true;
}

const defaultOptions: ICompareObjectsOptions = {
    returnDifferences: false,
    propertyFilter: alwaysTrue
};

export function compareObjects<T extends {}>(left: T | null | undefined, right: T | null | undefined, options: Partial<ICompareObjectsOptions>): ICompareObjectsResult {
    const appliedOptions = { ...defaultOptions, ...options };
    if(appliedOptions.returnDifferences) {
        const differences: IDifference[] = [];
        const result = areDifferent(left, right, appliedOptions, [], differences);
        return { areEqual: !result, differences };
    }
    const result = areDifferent(left, right, appliedOptions, []);
    return { areEqual: !result, differences: [] };
}
